import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FileEdit, Plus, Archive, ArchiveRestore, Trash2, Search, X, FileText,
  Pencil, Eye, EyeOff, BookOpen, Settings2,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { WsPageTitle } from "./WorkspaceLayout";
import WorkspaceEditorModal from "../../components/admin/WorkspaceEditorModal";
import { SingleRichEditor } from "../../components/admin/RichEditor";
import { Field } from "../../components/admin/Fields";
import AspectCropUpload from "../../components/admin/AspectCropUpload";
import CategoryManager, { CategorySelect, CategoryBadge, CategorySidebar, useCategories, matchCategoryFilter } from "../../components/workspace/CategoryManager";
import WritingPreview from "./WritingPreview";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

const empty = () => ({
  id: null, title: "", body: "", status: "draft", tags: [],
  category: "", cover_url: "", story_url: "", kind: "deneme",
});

export default function Writings() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [archived, setArchived] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [active, setActive] = useState(empty());
  const [dirty, setDirty] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [catManagerOpen, setCatManagerOpen] = useState(false);

  const { categories } = useCategories("writings");

  const { data = [] } = useQuery({
    queryKey: ["workspace", "writings", archived, statusFilter, q],
    queryFn: () => api.get("/admin/workspace/writings", {
      params: { archived: archived ? 1 : 0, status: statusFilter, q }
    }).then((r) => r.data),
  });

  const filtered = data.filter((w) => matchCategoryFilter(w.category, categoryFilter, categories));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["workspace", "writings"] });
    qc.invalidateQueries({ queryKey: ["workspace", "overview"] });
  };

  const openNew = () => { setActive(empty()); setDirty(false); setModalOpen(true); };
  const openEdit = (w) => { setActive({ ...empty(), ...w }); setDirty(false); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setDirty(false); };
  const setField = (k, v) => { setActive((a) => ({ ...a, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!active.title.trim() && !active.body.trim()) {
      toast.error("Başlık veya içerik boş olamaz");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: active.title, body: active.body, status: active.status,
        tags: active.tags, category: active.category,
        cover_url: active.cover_url, story_url: active.story_url,
        kind: active.kind || "deneme",
      };
      if (active.id) {
        await api.put(`/admin/workspace/writings/${active.id}`, payload);
        toast.success("Yazı güncellendi");
      } else {
        const { data: created } = await api.post("/admin/workspace/writings", payload);
        setActive(created);
        toast.success("Yazı oluşturuldu");
      }
      setDirty(false);
      invalidate();
      setModalOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const archive = async (id, restore = false) => {
    try {
      await api.post(`/admin/workspace/writings/${id}/archive`, null, { params: { restore: restore ? 1 : 0 } });
      toast.success(restore ? "Geri yüklendi" : "Arşive alındı");
      invalidate();
      if (previewId === id) setPreviewId(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Yazı kalıcı olarak silinecek. Onaylıyor musunuz?")) return;
    try {
      await api.delete(`/admin/workspace/writings/${id}`);
      toast.success("Silindi");
      invalidate();
      if (previewId === id) setPreviewId(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || active.tags.includes(t)) { setTagInput(""); return; }
    setActive((a) => ({ ...a, tags: [...a.tags, t] }));
    setTagInput("");
    setDirty(true);
  };

  const activePreview = previewId ? data.find((w) => w.id === previewId) : null;

  // Auto-pick the first writing when previewMode is turned on
  useEffect(() => {
    if (previewMode && !previewId && filtered.length > 0) {
      setPreviewId(filtered[0].id);
    }
    if (!previewMode) setPreviewId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode]);

  return (
    <div data-testid="ws-writings">
      <WsPageTitle
        icon={FileEdit}
        title="Yazılarım"
        sub="Mektup, konuşma, deneme ve uzun form yazıların."
        action={
          <>
            <label className="flex items-center gap-2 text-xs bg-white rounded-full border border-indigo-200 px-3 py-1.5 shadow-sm">
              {previewMode ? <Eye size={14} className="text-indigo-600" /> : <EyeOff size={14} className="text-stone-400" />}
              <span className={previewMode ? "font-semibold text-indigo-700" : "text-stone-500"}>Önizleme modu</span>
              <Switch checked={previewMode} onCheckedChange={setPreviewMode} data-testid="ws-writing-preview-toggle" />
            </label>
            <Button onClick={openNew} data-testid="ws-writing-new">
              <Plus size={14} className="mr-1" />Yeni yazı
            </Button>
          </>
        }
      />

      {/* Sidebar + content */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4" data-testid="ws-writings-layout">
        <CategorySidebar
          kind="writings"
          items={data}
          value={categoryFilter}
          onChange={setCategoryFilter}
          onManage={() => setCatManagerOpen(true)}
          accent="rose"
          allLabel="Tüm Yazılar"
        />

        <div className="min-w-0 space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-rose-100 p-3 shadow-sm flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={13} className="absolute left-2.5 top-2.5 text-stone-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Yazılarda ara..." className="pl-7 h-9 text-sm" data-testid="ws-writing-search" />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
              {[["", "Hepsi"], ["draft", "Taslak"], ["final", "Final"]].map(([v, l]) => (
                <button key={v} onClick={() => setStatusFilter(v)} className={`px-2.5 py-1.5 rounded-full border ${statusFilter === v ? "bg-rose-50 border-rose-300 text-rose-800 font-semibold" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                  {l}
                </button>
              ))}
              <button onClick={() => setArchived((v) => !v)} className={`px-2.5 py-1.5 rounded-full border ${archived ? "bg-stone-100 border-stone-300 font-semibold" : "border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                <Archive size={10} className="inline mr-1" />{archived ? "Arşiv" : "Aktif"}
              </button>
            </div>
          </div>

      {/* Split view OR Grid */}
      {previewMode ? (
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-3 min-h-[70vh]" data-testid="ws-writings-split">
          <aside className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden flex flex-col max-h-[70vh]">
            <div className="px-3 py-2 bg-rose-50 border-b border-rose-100 text-xs font-semibold text-rose-800">
              {filtered.length} yazı
            </div>
            <ul className="flex-1 overflow-y-auto">
              {filtered.length === 0 && (
                <li className="p-6 text-xs text-stone-400 italic text-center">Yazı yok.</li>
              )}
              {filtered.map((w) => (
                <li key={w.id}>
                  <button
                    onClick={() => setPreviewId(w.id)}
                    className={`w-full text-left px-3 py-2.5 border-b border-stone-100 flex items-start gap-2 ${
                      previewId === w.id ? "bg-rose-50 border-l-4 border-l-rose-500" : "hover:bg-stone-50"
                    }`}
                    data-testid={`ws-writing-pick-${w.id}`}>
                    <BookOpen size={14} className={`mt-0.5 shrink-0 ${previewId === w.id ? "text-rose-600" : "text-stone-400"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{w.title || "(başlıksız)"}</div>
                      <div className="text-[10px] text-stone-500 mt-0.5">
                        {w.word_count || 0} kelime · {fmtDate(w.updated_at)}
                      </div>
                      {w.category && (
                        <div className="mt-1">
                          <CategoryBadge name={w.category} color={categories.find((c) => c.name === w.category)?.color || "rose"} />
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <section className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden max-h-[82vh]">
            <WritingPreview
              writing={activePreview}
              onBack={() => setPreviewId(null)}
              onEdit={(w) => { setPreviewMode(false); openEdit(w); }}
            />
          </section>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-rose-200 p-10 text-center text-stone-500">
          <FileEdit size={28} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Henüz yazı yok.</p>
          <Button onClick={openNew} className="mt-3" size="sm"><Plus size={13} className="mr-1" />İlk yazını oluştur</Button>
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="ws-writings-grid">
          {filtered.map((w) => {
            const catColor = categories.find((c) => c.name === w.category)?.color || "rose";
            const isFinal = w.status === "final";
            return (
              <li key={w.id}
                  className="relative bg-white rounded-lg border border-rose-100/70 hover:border-rose-300 shadow-sm hover:shadow-lg transition-all overflow-hidden group flex"
                  data-testid={`ws-writing-item-${w.id}`}>
                {/* Accent strip left — thin cover image or gradient */}
                {w.cover_url ? (
                  <div className="w-16 shrink-0 bg-gradient-to-br from-rose-100 to-amber-50 overflow-hidden">
                    <img src={w.cover_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : (
                  <div className={`w-1.5 shrink-0 ${isFinal ? "bg-emerald-500" : "bg-gradient-to-b from-rose-400 to-orange-400"}`} />
                )}

                {/* Body */}
                <button
                  onClick={() => { setPreviewMode(true); setPreviewId(w.id); }}
                  className="flex-1 min-w-0 text-left p-3 pr-14"
                  data-testid={`ws-writing-preview-${w.id}`}
                >
                  <h3 className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2 group-hover:text-rose-700 transition-colors">
                    {w.title || "(başlıksız)"}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-stone-500 flex-wrap">
                    <span className="tabular-nums">{w.word_count || 0} söz</span>
                    <span className="text-stone-300">·</span>
                    <span>{fmtDate(w.updated_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {isFinal && (
                      <span className="text-[9px] font-bold tracking-wider inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> FINAL
                      </span>
                    )}
                    {w.category && (
                      <CategoryBadge name={w.category} color={catColor} />
                    )}
                  </div>
                </button>

                {/* Hover actions (abs-positioned, no visual noise when idle) */}
                <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={(e) => { e.stopPropagation(); openEdit(w); }}
                    className="p-1.5 rounded-md bg-white/95 border border-stone-200 text-stone-600 hover:text-rose-700 hover:border-rose-300 shadow-sm"
                    title="Düzenle"
                    data-testid={`ws-writing-edit-${w.id}`}
                  >
                    <Pencil size={12} />
                  </button>
                  {!archived ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); archive(w.id); }}
                      className="p-1.5 rounded-md bg-white/95 border border-stone-200 text-stone-600 hover:text-amber-700 hover:border-amber-300 shadow-sm"
                      title="Arşive al"
                    >
                      <Archive size={12} />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); archive(w.id, true); }}
                      className="p-1.5 rounded-md bg-white/95 border border-stone-200 text-stone-600 hover:text-indigo-700 hover:border-indigo-300 shadow-sm"
                      title="Geri yükle"
                    >
                      <ArchiveRestore size={12} />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(w.id); }}
                    className="p-1.5 rounded-md bg-white/95 border border-stone-200 text-red-600 hover:text-red-700 hover:border-red-300 shadow-sm"
                    title="Sil"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
        </div>
      </div>

      {/* Modal */}
      <WorkspaceEditorModal
        open={modalOpen}
        onClose={closeModal}
        title={active.id ? "Yazıyı düzenle" : "Yeni yazı"}
        icon={FileEdit}
        size="xl"
        busy={busy}
        dirty={dirty}
        onSubmit={save}
        submitLabel={active.id ? "Güncelle" : "Oluştur"}
        testid="ws-writing-modal"
        accent="rose"
      >
        <div className="grid lg:grid-cols-[1fr,280px] gap-4">
          <div className="space-y-3">
            <Input
              value={active.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Yazı başlığı..."
              className="text-lg font-semibold"
              data-testid="ws-writing-title"
            />
            <SingleRichEditor
              value={active.body}
              onChange={(v) => setField("body", v)}
              testid="ws-writing-body"
              minH="min-h-[380px]"
            />
          </div>

          <div className="space-y-3">
            <Field label="Durum">
              <select
                value={active.status}
                onChange={(e) => setField("status", e.target.value)}
                className="w-full h-9 border rounded-md px-3 text-sm bg-white"
                data-testid="ws-writing-status"
              >
                <option value="draft">Taslak</option>
                <option value="final">Final</option>
              </select>
            </Field>

            <Field label="Kategori">
              <CategorySelect
                value={active.category}
                onChange={(v) => setField("category", v)}
                kind="writings"
                testid="ws-writing-category"
                onManage={() => setCatManagerOpen(true)}
              />
            </Field>

            <AspectCropUpload
              label="Kapak görseli (Facebook / OG)"
              hint="Facebook ve link önizlemelerinde kullanılır. 1200×630 oranına kırpılır, orijinal çözünürlük korunur."
              value={active.cover_url}
              onChange={(url) => setField("cover_url", url)}
              aspect={1200 / 630}
              aspectLabel="1200×630"
              testid="ws-writing-cover"
              previewClass="w-full h-32 rounded-lg"
              accent="rose"
            />

            <AspectCropUpload
              label="Hikaye görseli (Instagram Story)"
              hint="Opsiyonel: Instagram/Facebook hikaye paylaşımları için dikey görsel. 1080×1920 oranına kırpılır."
              value={active.story_url}
              onChange={(url) => setField("story_url", url)}
              aspect={1080 / 1920}
              aspectLabel="1080×1920"
              testid="ws-writing-story"
              previewClass="w-28 h-48 rounded-lg mx-auto"
              accent="pink"
            />

            <Field label="Etiketler">
              <div className="flex items-center gap-1 flex-wrap p-2 border rounded-md bg-white min-h-[42px]">
                {active.tags.map((t, i) => (
                  <span key={i} className="text-[11px] bg-rose-50 text-rose-800 border border-rose-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                    #{t}
                    <button type="button" onClick={() => { setActive((a) => ({ ...a, tags: a.tags.filter((_, j) => j !== i) })); setDirty(true); }} className="hover:text-red-600"><X size={10} /></button>
                  </span>
                ))}
                <input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="etiket ekle…"
                  className="text-[12px] outline-none bg-transparent flex-1 min-w-[80px]"
                  data-testid="ws-writing-tag-input"
                />
              </div>
            </Field>

            <div className="text-[11px] text-stone-500 flex items-center gap-1 pt-2 border-t">
              <FileText size={11} />
              {(active.body || "").replace(/<[^>]*>/g, "").trim().split(/\s+/).filter(Boolean).length} kelime
            </div>
          </div>
        </div>
      </WorkspaceEditorModal>

      <CategoryManager
        open={catManagerOpen}
        onClose={() => setCatManagerOpen(false)}
        kind="writings"
        title="Yazı kategorilerini yönet"
      />
    </div>
  );
}
